import { useTheme } from "@mui/material";
import * as React from "react";

import { EventRecord } from "@/shared/domain/events";
import {
  Dimensions,
  OnDimensionsChange,
  useDebouncedCallback,
  useMeasure,
} from "@/shared/hooks";
import { DEFAULT_PLOT_PANEL_CONFIG } from "@/shared/state/visualization/context";
import { useEventConfigs } from "@/shared/state/visualization/hooks";
import {
  EventConfig,
  PlotPanelState,
  TimeBoundsSource,
} from "@/shared/state/visualization/schema/v1";

import { colorForAnnotation } from "../../../colorScale";
import {
  TimeBoundsSourceOptions,
  useTimeDisplayMode,
  useWorkspaceDatasetIdsForFiles,
  useWorkspaceTimeBounds,
} from "../../../WorkspaceCtx/hooks";
import { useWorkspaceEvents } from "../../../WorkspaceCtx/useWorkspaceEvents";
import { SeriesAlignment } from "../alignment";
import { SetStatePayload, TimeSpanAnnotation } from "../messaging/commands";
import { usePlotPanelContext } from "../panelContext/usePlotPanelContext";
import { PlotManager } from "../PlotManager";
import { PlotTool } from "../plotTools";

import { usePaddedTimeBounds } from "./usePaddedTimeBounds";

function eventsToPlotAnnotations(
  events: EventRecord[],
  eventsConfig: Record<string, EventConfig> | undefined,
  showEvents: boolean,
): TimeSpanAnnotation[] {
  if (!showEvents) {
    return [];
  }

  return events
    .filter((event) => {
      const eventConfig = eventsConfig?.[event.event_id] ?? { isVisible: true };
      return eventConfig.isVisible;
    })
    .map((event) => ({
      annotationId: event.event_id,
      startTime: event.start_time,
      endTime: event.end_time,
      style: {
        labelText: event.name,
        labelColor: "black",
        backgroundColor: colorForAnnotation(
          event.name,
          0.2,
          event.display_options?.color,
        ),
      },
    }));
}

const zoomInteractionConfig = {
  speed: 0.1, // 10% per zoom
};

/**
 * Sets aside some time for users to continuously scroll the mouse wheel/trackpad
 * without toggling the zoom interaction as done/not done/done/not done repeatedly.
 *
 * This is needed because scrolling continuously generates discrete mouse events without
 * a clear start/stop signal.  In contrast with panning, where the mouse (or finger) has to
 * click/touch before the motion events, and it has to unclick/untouch afterwards.
 */
const ZOOM_END_DELAY_MS = 100;

export function useZoom(
  plotManager: PlotManager | null,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  setIsZooming: (isZooming: boolean) => void,
) {
  const { activeTool, zoomMode } = usePlotPanelContext();
  const zoomEndTimeoutRef = React.useRef<number>();

  React.useEffect(
    function addPlotZoomListener() {
      const canvas = canvasRef.current;
      if (canvas === null || plotManager === null) {
        setIsZooming(false);
        return;
      }

      if (activeTool !== PlotTool.Move) {
        setIsZooming(false);
        return;
      }

      const abortController = new AbortController();
      canvas.addEventListener(
        "wheel",
        function zoom(event: WheelEvent) {
          // Prevent the event from triggering the default scroll behavior
          if (event.cancelable) {
            event.preventDefault();
          }

          const { target } = event;

          if (target instanceof Element) {
            const rect = target.getBoundingClientRect();
            const offsetX = event.clientX - rect.left;
            const offsetY = event.clientY - rect.top;

            const center = {
              x: offsetX,
              y: offsetY,
            };

            let zoomSpeedRatio = zoomInteractionConfig.speed;

            // Swap the sign of the zoom speed if the user is zooming out
            if (event.deltaY >= 0) {
              zoomSpeedRatio = -zoomSpeedRatio;
            }

            setIsZooming(true);
            zoomEndTimeoutRef.current = window.setTimeout(function endZoom() {
              setIsZooming(false);
            }, ZOOM_END_DELAY_MS);
            plotManager.zoom({
              zoomRatioX: zoomSpeedRatio,
              zoomRatioY: zoomSpeedRatio,
              center,
              axis: zoomMode,
            });
          }
        },
        { signal: abortController.signal, passive: false },
      );

      return function dispose() {
        abortController.abort();
        window.clearTimeout(zoomEndTimeoutRef.current);
        setIsZooming(false);
      };
    },
    [plotManager, canvasRef, activeTool, zoomMode, setIsZooming],
  );
}

export function usePan(
  plotManager: PlotManager | null,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  setIsDragging: (isDragging: boolean) => void,
) {
  const { activeTool } = usePlotPanelContext();

  React.useEffect(
    function addPlotPanListeners() {
      const canvas = canvasRef.current;
      if (canvas === null || plotManager === null) {
        return;
      }

      if (activeTool !== PlotTool.Move) {
        return;
      }

      const abortController = new AbortController();

      canvas.addEventListener(
        "pointerdown",
        function panStart(event: PointerEvent) {
          const eventAbortController = new AbortController();

          let x: number | null = event.clientX;
          let y: number | null = event.clientY;

          canvas.addEventListener(
            "pointermove",
            function pan(event: PointerEvent) {
              if (x === null || y === null) {
                // Set x and y then return for next time
                x = event.clientX;
                y = event.clientY;
                return;
              }

              setIsDragging(true);
              canvas.style.cursor = "grabbing";

              const deltaX = event.clientX - x;
              const deltaY = event.clientY - y;

              plotManager.pan({ deltaX, deltaY });

              // Save the current pointer position for the next difference
              x = event.clientX;
              y = event.clientY;
            },
            {
              signal: eventAbortController.signal,
            },
          );

          canvas.addEventListener(
            "pointerup",
            function panEnd() {
              // Clean up event listeners using abort controller
              eventAbortController.abort();

              canvas.style.cursor = "crosshair";

              // Reset closure variables for next drag event
              x = null;
              y = null;

              /*
              Schedules setting isDragging to false on the next iteration of the event loop
              after all the immediate listeners have been called. This is to prevent
              subsequent click events from occurring at the same time as when the drag completes.
            */
              setTimeout(function resetDragging() {
                setIsDragging(false);
              }, 0);
            },
            { once: true },
          );

          const panCancel = () => {
            // Clean up event listeners using abort controller
            eventAbortController.abort();

            // Reset closure variables for next drag event
            x = null;
            y = null;

            setIsDragging(false);
            canvas.style.cursor = "crosshair";
          };

          canvas.addEventListener("pointercancel", panCancel, { once: true });
          canvas.addEventListener("pointerleave", panCancel, { once: true });
        },
        { signal: abortController.signal },
      );

      return function dispose() {
        abortController.abort();
      };
    },
    [plotManager, canvasRef, setIsDragging, activeTool],
  );
}

/**
 * Resize the plot when the canvas is resized
 */
export function useMeasureCanvas(plotManager: PlotManager | null) {
  const onRenderingSurfaceResize = React.useCallback(
    ({ width, height }: Dimensions) => {
      if (plotManager === null) {
        return;
      }

      plotManager.resize({ width, height });
    },
    [plotManager],
  );

  const onRenderingSurfaceResizeDebounced =
    useDebouncedCallback<OnDimensionsChange>(onRenderingSurfaceResize, 150);

  const [measured] = useMeasure<HTMLDivElement>({
    onDimensionsChange: onRenderingSurfaceResizeDebounced,
  });

  return measured;
}

/**
 * Sends the panel's series, time bounds, time display mode, and either the
 * event annotations to draw or the resolved alignment to the plot worker
 * whenever any of them change. Nothing is sent until the panel's time bounds
 * resolve.
 *
 * An aligned panel builds no annotations: {@link SetStatePayload} admits them
 * only alongside absolute time.
 */
export function useOnStateUpdated(
  plotManager: PlotManager | null,
  state: PlotPanelState,
  alignment: SeriesAlignment | undefined,
) {
  const [fileIds, topicIds, messagePathIds] = React.useMemo(() => {
    const fileIds: string[] = [];
    const topicIds: string[] = [];
    const messagePathIds: string[] = [];

    state.data.forEach((series) => {
      fileIds.push(series.data.topic.association.association_id);
      topicIds.push(series.data.topic.id);
      messagePathIds.push(series.data.messagePath.id);
    });

    return [fileIds, topicIds, messagePathIds];
  }, [state.data]);

  const datasetIds = useWorkspaceDatasetIdsForFiles(fileIds);

  const timeBoundsSource =
    state.config?.timeBoundsSource ||
    DEFAULT_PLOT_PANEL_CONFIG.timeBoundsSource;
  const timeBoundsOptions = React.useMemo((): TimeBoundsSourceOptions => {
    switch (timeBoundsSource) {
      case TimeBoundsSource.FilesInWorkspace:
        return { timeBoundsSource };
      case TimeBoundsSource.FilesInPanel:
        return { timeBoundsSource, fileIds };
      case TimeBoundsSource.TopicsInPanel:
        return { timeBoundsSource, topicIds };
    }
  }, [timeBoundsSource, fileIds, topicIds]);

  const timeBounds = usePaddedTimeBounds(
    useWorkspaceTimeBounds(timeBoundsOptions),
  );
  const [timeDisplayMode, _] = useTimeDisplayMode();

  const eventsQuery = useWorkspaceEvents({
    select: {
      datasetIds,
      fileIds,
      topicIds,
      messagePathIds,
    },
  });

  const showEvents =
    state.config?.showEvents ?? DEFAULT_PLOT_PANEL_CONFIG.showEvents;

  const eventConfigs = useEventConfigs();

  React.useEffect(
    function updatePlotState() {
      if (
        plotManager === null ||
        timeBounds.earliest === undefined ||
        timeBounds.latest === undefined
      ) {
        return;
      }
      const abortController = new AbortController();
      const payload: SetStatePayload =
        alignment === undefined
          ? {
              state: state.data,
              timeBounds,
              timeDisplayMode,
              annotations: eventsToPlotAnnotations(
                eventsQuery.data ?? [],
                eventConfigs,
                showEvents,
              ),
            }
          : { state: state.data, timeBounds, timeDisplayMode, alignment };

      plotManager.setState(payload, { signal: abortController.signal });

      return () => {
        abortController.abort();
      };
    },
    [
      plotManager,
      state.data,
      timeBounds,
      timeDisplayMode,
      eventsQuery.data,
      eventConfigs,
      showEvents,
      alignment,
    ],
  );
}

/**
 * Update the plot style when the theme (light/dark mode) changes
 */
export function useOnThemeChanged(plotManager: PlotManager | null) {
  const theme = useTheme();
  React.useEffect(
    function updatePlotStyle() {
      if (plotManager === null) {
        return;
      }

      plotManager.setStyle({ mode: theme.palette.mode });
    },
    [plotManager, theme.palette.mode],
  );
}
